import { AnalyticsService } from '@/services';
import {
  playerSessionsSelectors,
  selectActiveModalParams,
  selectEpisodeCompliance,
  selectEpisodeProgress,
  uiActions,
  useAppDispatch,
  useAppSelector,
  useFetchEpisodeQuery,
  useFindPlayerSessionByEpisodeIdQuery,
  useSubmitSessionFeedbackMutation,
} from '@/store';
import { EpisodeFeedback } from '@/types';
import { SnackbarUtils } from '@/utils';

import { EpisodeFeedbackModalContent as EpisodeFeedbackModalContentComponent } from './EpisodeFeedbackModalContent.component';

export const EpisodeFeedbackModalContent = () => {
  const dispatch = useAppDispatch();

  const { episodeId, chapterId: activeChapterId } =
    useAppSelector(selectActiveModalParams) || {};
  const compliance = useAppSelector(state =>
    selectEpisodeCompliance(state, episodeId),
  );
  const playerSession = useAppSelector(state =>
    playerSessionsSelectors.selectById(state, episodeId),
  );
  const episodeChaptersProgress = useAppSelector(state =>
    selectEpisodeProgress(state, episodeId),
  );

  const { data: episode } = useFetchEpisodeQuery(
    { episodeId, compliance },
    { skip: !episodeId },
  );

  useFindPlayerSessionByEpisodeIdQuery(
    { episodeId },
    { skip: !episodeId, refetchOnMountOrArgChange: true },
  );

  const chapterProgress = episodeChaptersProgress?.find(
    ({ chapterId }) => chapterId === activeChapterId,
  );
  const episodePlayedDuration = playerSession?.playedDuration || 0;

  const [
    submitSessionFeedbackRequest,
    { isLoading: isSubmitting, isSuccess: isSubmitted },
  ] = useSubmitSessionFeedbackMutation();

  const submitFeedback = async ({
    feedback,
  }: {
    feedback: EpisodeFeedback;
  }) => {
    if (!playerSession) return;
    try {
      const request = submitSessionFeedbackRequest({
        sessionId: playerSession.sessionId,
        response: feedback,
        submittedAt: new Date().toISOString(),
      });

      await request.unwrap();
    } catch {
      SnackbarUtils.error(
        'Something went wrong. Please try again or contact support.',
      );
    }

    AnalyticsService.episodeFeedback({
      episodeRating: feedback.episodeRating,
      episodeId,
      chapterId: activeChapterId,
      chapterProgress: chapterProgress?.progress?.playedDuration || 0,
      episodePlayedDuration,
      textFeedback: feedback.textFeedback,
      ratingReasons: feedback.ratingReason.join(','),
    });
  };

  const onClose = () => {
    dispatch(uiActions.closeActiveModal());
  };

  return episode ? (
    <EpisodeFeedbackModalContentComponent
      episode={episode}
      isFeedbackSubmitted={isSubmitted}
      onClose={onClose}
      isSubmitting={isSubmitting}
      submitFeedback={submitFeedback}
    />
  ) : null;
};
